import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_BASE } from '../config';
import { adminJson } from '../utils/adminAuth';

// /health/overwatch lives at the API root, not under /api.
const HEALTH_URL = `${API_BASE.replace(/\/api\/?$/, '')}/health/overwatch`;

export default function AdminHealth() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [refreshedAt, setRefreshedAt] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    fetch(HEALTH_URL)
      .then(adminJson)
      .then(d => { setData(d); setRefreshedAt(new Date()); })
      .catch(() => setError('Failed to load health'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 60000); // auto-refresh every 60s
    return () => clearInterval(t);
  }, [load]);

  const alerts = (data?.alerts || []);
  const critical = alerts.filter(a => a.severity === 'CRITICAL' || a.severity === 'critical');
  const warnings = alerts.filter(a => !(a.severity === 'CRITICAL' || a.severity === 'critical'));
  const services = data?.services || {};

  const metric = (label, value) => (
    <div style={statCard}><div style={{ fontSize: 22, fontWeight: 700 }}>{value}</div><div style={muted}>{label}</div></div>
  );

  return (
    <div style={{ padding: 20, maxWidth: 1000, margin: '0 auto' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h2>System Health <span style={{ fontSize: 14, color: data?.healthy ? '#059669' : '#dc2626' }}>{data ? (data.healthy ? '● healthy' : '● issues') : ''}</span></h2>
      </div>
      <p style={muted}>Read-only view of the Overwatch monitor (server-side, refreshes every 15 min). To restart a service, use the desktop app.</p>
      {error && <div style={{ color: '#dc2626' }}>{error}</div>}
      {loading && !data ? <div>Loading…</div> : data && (
        <>
          {/* Alerts */}
          {!!critical.length && (
            <div style={{ ...alertBox, borderColor: '#dc2626', background: '#fef2f2' }}>
              <strong style={{ color: '#dc2626' }}>CRITICAL</strong>
              {critical.map((a, i) => <div key={i}>• {a.message}</div>)}
            </div>
          )}
          {!!warnings.length && (
            <div style={{ ...alertBox, borderColor: '#d97706', background: '#fffbeb' }}>
              <strong style={{ color: '#d97706' }}>Warnings</strong>
              {warnings.map((a, i) => <div key={i}>• {a.message}</div>)}
            </div>
          )}
          {!alerts.length && <div style={{ ...alertBox, borderColor: '#059669', background: '#f0fdf4', color: '#059669' }}>✓ All clear — no active alerts.</div>}

          {/* Services */}
          <h3>Services</h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(220px,1fr))', gap: 10 }}>
            {Object.entries(services).map(([name, status]) => (
              <div key={name} style={{ ...statCard, display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ color: status === 'active' || status === 'running' ? '#059669' : '#dc2626', fontSize: 18 }}>●</span>
                <div><div style={{ fontWeight: 600 }}>{name}</div><div style={muted}>{status}</div></div>
              </div>
            ))}
            {!Object.keys(services).length && <div style={muted}>No service data.</div>}
          </div>

          {/* Metrics */}
          <h3 style={{ marginTop: 18 }}>Server</h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(140px,1fr))', gap: 10 }}>
            {metric('Disk used', data.disk_pct != null ? `${data.disk_pct}%` : '—')}
            {metric('Memory used', data.mem_pct != null ? `${data.mem_pct}%` : '—')}
            {metric('DB connections', data.db_connections ?? '—')}
            {metric('Failed imports', `${data.new_failures ?? 0} new / ${data.total_failed ?? 0}`)}
            {metric('Pending feed media', data.pending_feed_media ?? '—')}
            {metric('Photo ghosts', data.photo_ghost_vehicles ?? '—')}
          </div>

          <div style={{ ...muted, marginTop: 16 }}>
            Overwatch last run: {data.timestamp ? new Date(data.timestamp).toLocaleString() : '—'} ·
            page refreshed: {refreshedAt ? refreshedAt.toLocaleTimeString() : '—'} ·
            <button onClick={load} style={{ ...linkBtn, marginLeft: 6 }}>refresh now</button>
          </div>
        </>
      )}
    </div>
  );
}

const linkBtn = { background: 'none', border: 'none', color: '#0078D4', cursor: 'pointer', padding: 0 };
const statCard = { border: '1px solid #e5e7eb', borderRadius: 8, padding: '12px 14px', background: '#fff' };
const muted = { fontSize: 12, color: '#6b7280' };
const alertBox = { border: '1px solid', borderRadius: 8, padding: '10px 14px', margin: '10px 0', fontSize: 14 };
